import type { GraphQLError } from 'graphql';
/**
 * Errors come in in with type unknown, but from the Sink interface:
 *
 * An error that has occured. Calling this function "closes" the sink.
 * Besides the errors being `Error` and `readonly GraphQLError[]`, it
 * can also be a `CloseEvent`, but to avoid bundling DOM typings because
 * the client can run in Node env too, you should assert the close event
 * type during implementation.
 *
 * error(error: unknown): void;
 */

export type SinkError = Error | readonly GraphQLError[] | CloseEvent | ErrorEvent;

/**
 * A plain `Error`, optionally carrying the close information of the `CloseEvent` it came from.
 *
 * `closeCode` and `wasClean` are set if, and only if, the input was a `CloseEvent`, so their
 * absence is a fact about the input. `closeCode` is the raw WebSocket/`graphql-ws` close code,
 * which lets callers tell a permanent failure from a retryable one without matching on `message`.
 *
 * Not named `code`: the `Error` and `GraphQLError` branches below pass their input through
 * untouched, and those errors routinely carry an unrelated `code` of their own (`ECONNRESET` and
 * friends), which would leave a caller unable to tell the two apart.
 */
export interface SinkErrorWithCloseInfo extends Error {
	closeCode?: number;
	wasClean?: boolean;
}

export const getSinkError = (error: SinkError): SinkErrorWithCloseInfo => {
	let errorInfo: SinkErrorWithCloseInfo;
	if (Array.isArray(error)) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		[errorInfo] = error as readonly GraphQLError[];
	} else if (error instanceof Error) {
		errorInfo = error;
	} else if (typeof error === 'object' && 'type' in error && error.type === 'close') {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const closeEvent = error as CloseEvent;
		errorInfo = new Error(`CloseEvent: ${closeEvent.reason}`);
		errorInfo.closeCode = closeEvent.code;
		errorInfo.wasClean = closeEvent.wasClean;
	} else if (typeof error === 'object' && 'type' in error && error.type === 'error') {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const errorEvent = error as ErrorEvent;
		errorInfo = new Error(`ErrorEvent: "${errorEvent.error}"`);
	} else if (error instanceof Event) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		errorInfo = new Error(`Event: ${(error as Event).type}`);
	} else {
		errorInfo = new Error(`Unknown sink error: ${error}`);
	}

	if (error instanceof Error && error.stack) {
		errorInfo.stack = error.stack;
	}

	return errorInfo;
};
